import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useNavigate } from '@tanstack/react-router';
import BatchUpdatesService from './batchUpdate.service';

type ActionType = 'createBatchUpdate' | 'updateBatchUpdate' | 'executeBatchUpdate' | 'deleteBatchUpdate';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

const useBatchUpdateMutation = (): {
  batchUpdate: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  batchUpdateMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  isUpdatingBatchUpdate: Record<string, boolean>;
  setIsUpdatingBatchUpdate: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
} => {
  const [isUpdatingBatchUpdate, setIsUpdatingBatchUpdate] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const batchUpdatesService = BatchUpdatesService();

    const action: Record<ActionType, (payload: any) => Promise<any>> = {
      createBatchUpdate: (params) => batchUpdatesService.postBatchUpdate(params.payload),
      updateBatchUpdate: (params) => batchUpdatesService.putBatchUpdateById(params.payload, params.data),
      executeBatchUpdate: (params) => batchUpdatesService.executeBatchUpdateById(params.payload),
      deleteBatchUpdate: (params) => batchUpdatesService.deleteBatchUpdateById(params.payload),
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType, ...newBatchUpdate }) => {
      setIsUpdatingBatchUpdate({ ...isUpdatingBatchUpdate, [actionType]: true });

      // Optimistic update for delete
      if (actionType === 'deleteBatchUpdate') {
        const previousBatchUpdates = queryClient.getQueryData(['batch-updates']);

        // Update the cache by filtering out the deleted item
        queryClient.setQueryData(['batch-updates'], (old: any) => {
          return old?.filter((batchUpdate: any) =>
            batchUpdate.massupdate_id !== newBatchUpdate.payload.id
          );
        });

        // Return context for rollback
        return { previousBatchUpdates };
      }
    },
    onSuccess: (data: any, variables: any) => {
      console.log('data:', data);
      navigate({ to: `/batch-updates` });
      if (variables.actionType === 'createBatchUpdate') {
        enqueueSnackbar('Batch Update created successfully', { variant: 'success' });
      } else if (variables.actionType === 'updateBatchUpdate') {
        enqueueSnackbar('Batch Update updated successfully', { variant: 'success' });
      } else if (variables.actionType === 'executeBatchUpdate') {
        enqueueSnackbar('Batch Update executed successfully', { variant: 'success' });
      } else if (variables.actionType === 'deleteBatchUpdate') {
        enqueueSnackbar('Batch Update deleted successfully', { variant: 'success' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during mutation:', error);
      if (variables.actionType === 'updateBatchUpdate') {
        enqueueSnackbar('Error updating Batch Update', { variant: 'error' });
      } else if (variables.actionType === 'executeBatchUpdate') {
        enqueueSnackbar('Error executing Batch Update', { variant: 'error' });
      } else if (variables.actionType === 'createBatchUpdate') {
        enqueueSnackbar('Error creating Batch Update', { variant: 'error' });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-updates'] });
      queryClient.invalidateQueries({ queryKey: ['batch-update-id'] });
    },
  });

  return {
    batchUpdate: mutation.mutate,
    batchUpdateMutation: mutation,
    isUpdatingBatchUpdate,
    setIsUpdatingBatchUpdate,
  };
};

export default useBatchUpdateMutation;
